/* eslint-disable react-hooks/set-state-in-effect, react-hooks/preserve-manual-memoization --
 * v77.99.77: ChildScopeContext seeds the selected-child state inside
 * an effect after fetching the child list, and memoises the derived
 * `selected` child for consumers. Both patterns are intentional and
 * audited; the experimental rules mis-flag them.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// v65 Phase 1 — child scope context.
//
// CONTRACT (v77.99.21):
// This component is the single frontend source of truth for "which
// children may the active user see?". It mirrors the backend's
// canonical resolver `services.child_scope.visible_child_ids_for_user`.
//
//   • The list is fetched from `/api/children`, which is itself
//     scoped server-side by `_visible_child_ids_for_user`. Anything
//     not in this list is invisible to the user.
//   • Pages, exports and child pickers MUST source their child list
//     from `useChildScope().children`. Do NOT fetch `/api/children`
//     directly and do NOT maintain a parallel list elsewhere.
//   • Server-side enforcement remains authoritative. Frontend
//     filtering protects UX but never replaces the backend gate
//     (which is the source of truth for permissions and exports).
//
// Persisted in localStorage per user so the selection survives reloads.
// The user-facing language never says "Space" or "scope" — only "All
// children" or the child's actual name.
//
// Phase 2 will replace the simple "selected child id" with a richer
// permissions-aware visible-children list, but the public API of this
// hook (`childScope.selectedChildId`, `childScope.children`,
// `childScope.setSelectedChildId`) stays the same so consumer pages
// don't have to change.
const ChildScopeContext = createContext(null);

const LS_KEY_FOR = (uid) => `cpc.childScope.selectedId:${uid || "anon"}`;

export function ChildScopeProvider({ children }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedIdState] = useState(null);

  // Hydrate the persisted selection on first mount + whenever the user changes.
  useEffect(() => {
    if (!user?.id) return;
    try {
      const cached = localStorage.getItem(LS_KEY_FOR(user.id));
      setSelectedIdState(cached || null);
    } catch (e) {
      console.warn("[childScope] localStorage hydrate failed:", e);
    }
  }, [user?.id]);

  // Refetch children whenever the user changes (login/logout/seat swap).
  // Mediators and operators don't need a child scope — they always see
  // everything within their own permission boundaries.
  const refetch = useCallback(async () => {
    if (!user?.id || user?.role === "mediator" || user?.is_primary_admin) {
      setList([]);
      setLoaded(true);
      return;
    }
    try {
      // v77.22.6 — Always exclude archived children from the global
      // child scope. They must never appear in any selector or filter
      // (Calendar, Messages, Expenses, Requests, Journal, Documents).
      // The Children page makes its own dedicated call (with
      // include_archived=true) for its archived section. We also
      // defensively dedupe by id at the source so an accidental
      // duplicate from upstream can never multiply into the UI.
      const r = await api.get("/children?include_archived=false");
      const raw = Array.isArray(r.data) ? r.data : [];
      const seen = new Set();
      const deduped = [];
      for (const c of raw) {
        if (c?.id && !seen.has(c.id)) {
          seen.add(c.id);
          deduped.push(c);
        }
      }
      setList(deduped);
    } catch (e) {
      console.warn("[childScope] children fetch failed:", e);
      setList([]);
    } finally {
      setLoaded(true);
    }
  }, [user?.id, user?.role, user?.is_primary_admin]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Drop a stale selection if the persisted child no longer exists
  // (e.g. it was deleted by the other parent).
  //
  // v76.3 — guard on `list.length > 0` so a transient empty list mid-
  // refetch (which happens on every hard reload before /api/children
  // returns) doesn't wipe a perfectly valid persisted selection. The
  // previous logic ran on the first render with `list = []` and cleared
  // the localStorage entry before refetch had a chance to populate it,
  // breaking cross-tab filter persistence on hard navigation.
  useEffect(() => {
    if (!loaded || !selectedId) return;
    if (list.length === 0) return;
    if (!list.some((c) => c.id === selectedId)) {
      setSelectedIdState(null);
      try { localStorage.removeItem(LS_KEY_FOR(user?.id)); } catch (e) { /* ignore */ }
    }
  }, [loaded, selectedId, list, user?.id]);

  const setSelectedId = useCallback((next) => {
    setSelectedIdState(next || null);
    try {
      if (next) localStorage.setItem(LS_KEY_FOR(user?.id), next);
      else localStorage.removeItem(LS_KEY_FOR(user?.id));
    } catch (e) {
      console.warn("[childScope] localStorage write failed:", e);
    }
  }, [user?.id]);

  const value = useMemo(() => {
    const selected = selectedId ? list.find((c) => c.id === selectedId) : null;
    return {
      // Master switch: null = "All children"
      selectedChildId: selectedId,
      selectedChild: selected || null,
      // The full child list this user can see today (Phase 1: every
      // child in the household). Phase 2 narrows this to the child
      // memberships the user actually owns.
      children: list,
      loaded,
      setSelectedChildId: setSelectedId,
      // Convenience: append `?child_id=…` to a relative path when a
      // child is selected. Existing query strings are preserved.
      withChildParam: (path) => {
        if (!selectedId) return path;
        const sep = path.includes("?") ? "&" : "?";
        return `${path}${sep}child_id=${encodeURIComponent(selectedId)}`;
      },
      // Convenience for axios config objects.
      params: selectedId ? { child_id: selectedId } : {},
      refetch,
    };
  }, [selectedId, list, loaded, setSelectedId, refetch]);

  return (
    <ChildScopeContext.Provider value={value}>
      {children}
    </ChildScopeContext.Provider>
  );
}

export function useChildScope() {
  const ctx = useContext(ChildScopeContext);
  if (!ctx) {
    // Soft-fail outside the provider so legacy pages don't crash during
    // staged rollout. Returns a no-op shape.
    return {
      selectedChildId: null,
      selectedChild: null,
      children: [],
      loaded: false,
      setSelectedChildId: () => {},
      withChildParam: (p) => p,
      params: {},
      refetch: () => Promise.resolve(),
    };
  }
  return ctx;
}
